import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { stringifyValues } from '@atc/string-fns';

import { Glyphicon } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { setItem } from '@/utilities/sessionStorageUtils';
import { SRP_PATH_KEY } from '@/utilities/srpPathFromSessionStorageUtils';

import { keywordSearchSubmit } from '@/analytics/srpAnalyticsHandlers';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import SuggestedKeywordFetcher from '@/fetchers/srp/SuggestedKeywordFetcher';

import useSrpNavigation from '@/hooks/useSrpNavigation';

const KeywordSearch = dynamic(() => import('@/components/KeywordSearch'), { ssr: false });

const navigateToArticlePage = (contentUrl) => {
    window.open(contentUrl);
};

export function KeywordSearchTrigger({ onTriggerClick = () => {} }) {

    const dispatch = useDispatch();

    const {
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
    } = useFeatures(['suggested_keyword']);

    return enableKeyword && (
        <div
            className="autocomplete-search padding-0 display-flex align-items-center justify-content-between"
        >
            <Glyphicon
                glyph="ai-search"
                className="text-accent padding-3 text-size-500"
                style={{ position: 'relative', marginRight: -48, zIndex: 1 }}
            />
            <div className="flex-grow react-autosuggest__container autocomplete">
                <input
                    type="text"
                    className="form-control"
                    data-cmp="cntnr-kywrd"
                    aria-autocomplete="list"
                    placeholder={keywordPlaceholder}
                    onFocus={() => {
                        dispatch(activeInteractionDuck.creators.setKeys({
                            showKeywordSearchModal: true,
                        }));
                        onTriggerClick();
                    }}
                    aria-label={keywordPlaceholder}
                />
            </div>
        </div>
    );
}

function KeywordSearchModalContainer({
    isStickyHeader = false,
    suppressClickEvent = false,
    onSearch = () => {},
    ...rest
}) {
    const dispatch = useDispatch();

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const query = useSelector(queryDuck.selectors.getDuckState);

    const filters = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const searchRadius = stringifyValues(_get(filters, 'searchRadius', 0));

    // Check if we're on a VDP page by looking for active inventory
    const vdpActiveInventory = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const isOnVdp = !!vdpActiveInventory?.id;

    // Build location object used for KeywordSearch component using query state (or filters for the SRP)
    const keywordSearchLocation = {
        zip: query.zip || _get(filters, 'zip', null),
        searchRadius: query.searchRadius || searchRadius,
    };

    const {
        advanced_keyword_search: [, { with_type_ahead: enableTypeAhead }],
        srp_category_search: [enableCategorySearch],
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
    } = useFeatures(['advanced_keyword_search', 'srp_category_search', 'suggested_keyword']);

    const navigateToSrp = useSrpNavigation();

    const handleKeywordSearch = useCallback(async (data) => {
        const { codes = {}, contentUrl } = data;

        if (!suppressClickEvent) {
            await sendClick(keywordSearchSubmit, null, {
                ...data,
                cmp: 'fts_kyw',
            });
        }

        onSearch(data);

        if (contentUrl) {
            navigateToArticlePage(contentUrl);
        } else {
            // Clear scroll position from session storage when navigating from VDP
            // This prevents the SRP from scrolling to the previous listing position
            if (isOnVdp) {
                setItem(SRP_PATH_KEY, {
                    path: '/cars-for-sale', // Use a generic path
                    scrollPosition: { left: 0, top: 0 },
                }, true);
            }

            navigateToSrp({
                action: 'replace',
                scroll: isOnVdp ? !isXs : false,
                scrollToResultsCount: isOnVdp ? isXs : false,
                resetPagination: true,
                filtersValues: { ...codes },
            });
            if (enableCategorySearch) {
                dispatch(srpActiveInteractionDuck.creators.setKeys({
                    activeCategory: 'All Results',
                }));
                dispatch(queryDuck.creators.setKeys({
                    categorySearch: 'allResults',
                }));
            }
        }
    }, [dispatch, enableCategorySearch, navigateToSrp, onSearch, suppressClickEvent, isOnVdp, isXs]);

    return enableKeyword && (
        <div
            data-cmp={`cntnr-kywrd${isStickyHeader ? '-sticky' : ''}`}
            {...rest}
        >
            <KeywordSearch
                autoFocus
                className={clsx('text-left', { 'advanced-keyword-search': enableTypeAhead })}
                fetchSuggestions={SuggestedKeywordFetcher}
                location={keywordSearchLocation}
                onSearch={handleKeywordSearch}
                placeholder={keywordPlaceholder}
                variant="modal"
            />
        </div>
    );
}

export default React.memo(KeywordSearchModalContainer, _isEqual);
